import styled from "styled-components";
import {
  narrowScreenMediaQuery,
  outerContainerCss,
  brand_orange_deep,
  brand_gradient,
} from "../constants/styles";
import useFadeInOnEnter from "./useFadeInOnEnter";

const Wrap = styled.section`
  background: ${({ $bg }) => $bg || "#ffffff"};
  padding: 72px 0;
  scroll-margin-top: 96px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: ${({ $visible }) =>
    $visible ? "translateY(0)" : "translateY(16px)"};
  transition:
    opacity 500ms ease,
    transform 500ms ease;

  @media ${narrowScreenMediaQuery} {
    padding: 48px 0;
  }

  &[data-theme="dark"],
  [data-theme="dark"] & {
    background: ${({ $bg }) => $bg || "#0f172a"};
  }
`;

const Inner = styled.div`
  ${outerContainerCss}
`;

export const SectionTitle = styled.h2`
  font-size: 40px;
  font-weight: 800;
  color: #1f2937;
  margin: 0 auto 12px;
  text-align: center;
  letter-spacing: -0.01em;
  position: relative;
  width: fit-content;
  padding-bottom: 14px;

  &::after {
    content: "";
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: 0;
    width: 72px;
    height: 4px;
    border-radius: 2px;
    background: ${brand_gradient};
  }

  /* "Taxsam.Community" is a single unbreakable token: at 28px it measured ~276px
   * against a 265px content box on a 320px screen, so it escaped the container and
   * forced the document into horizontal scroll.
   *
   * clamp() scales it instead of breaking it - a brand word split as
   * "Taxsam.Communit / y" is worse than a slightly smaller heading. At 373px and up
   * this still resolves to 28px, so nothing changes on the phone sizes that already
   * fit; only below that does it shrink, which is exactly where it was overflowing.
   *
   * max-width and overflow-wrap are the backstop for any future title long enough
   * to overflow even at the minimum size.
   */
  @media ${narrowScreenMediaQuery} {
    font-size: clamp(22px, 7.5vw, 28px);
    max-width: 100%;
    overflow-wrap: anywhere;
  }

  &[data-theme="dark"],
  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

export const SectionSubtitle = styled.p`
  color: #6b7280;
  font-size: 14px;
  max-width: 720px;
  margin: 0 auto 48px;
  text-align: center;
  line-height: 1.7;

  [data-theme="dark"] & {
    color: #d1d5db;
  }
`;

export const GroupTitle = styled.h3`
  font-size: 22px;
  font-weight: 800;
  color: #1f2937;
  margin: 0 0 24px;
  letter-spacing: -0.005em;
  position: relative;
  padding-left: 14px;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 4px;
    bottom: 4px;
    width: 4px;
    border-radius: 2px;
    background: ${brand_orange_deep};
  }

  @media ${narrowScreenMediaQuery} {
    font-size: 18px;
  }

  &[data-theme="dark"],
  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const GroupBlock = styled.div`
  padding-bottom: 40px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 40px;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }

  &[data-theme="dark"],
  [data-theme="dark"] & {
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }
`;

export function SectionGroup({ title, children }) {
  return (
    <GroupBlock>
      <GroupTitle>{title}</GroupTitle>
      {children}
    </GroupBlock>
  );
}

export default function SectionShell({ children, bg, id }) {
  const [ref, visible] = useFadeInOnEnter();
  return (
    <Wrap ref={ref} $bg={bg} id={id} $visible={visible}>
      <Inner>{children}</Inner>
    </Wrap>
  );
}
